import {
  type ComposerSeed,
  type ExistingComposerDraft,
} from '@littlebird/action-cards';
import { useEffect, useRef } from 'react';

import { logError } from '@/lib/errorLogging';

import {
  mergeSeedIntoDraft,
  nextConsumedKey,
  resolveActionCardSeed,
  shouldConsumeSeed,
} from './action-card-seed';

import type { ActionCard } from '@littlebird/sdk';

export type ActionCardPrefillSearch = {
  readonly auto_prompt?: string;
  readonly source_action_card?: string;
};

export type ActionCardPrefillSource = {
  get(id: string): ActionCard | undefined;
  readonly isLoaded: boolean;
};

export type ActionCardPrefillSink = {
  getDraft(draftId: string): ExistingComposerDraft | undefined;
  updateDraft(partial: { draftId: string } & Partial<ComposerSeed>): unknown;
};

export const useActionCardPrefill = ({
  search,
  draftId,
  actionCards,
  drafts,
  clearSearch,
}: {
  search: ActionCardPrefillSearch;
  draftId: string;
  actionCards: ActionCardPrefillSource;
  drafts: ActionCardPrefillSink;
  clearSearch: () => void;
}): void => {
  const sourceActionCardId = search.source_action_card;
  const autoPrompt = search.auto_prompt;
  const card = sourceActionCardId
    ? actionCards.get(sourceActionCardId)
    : undefined;
  const storeLoaded = actionCards.isLoaded;
  const consumedKeyRef = useRef<string | undefined>(undefined);
  const runRef = useRef(0);
  const writeChainRef = useRef(new Map<string, Promise<void>>());

  useEffect(() => {
    const searchKey = sourceActionCardId ?? autoPrompt;
    consumedKeyRef.current = nextConsumedKey(consumedKeyRef.current, searchKey);
    if (!shouldConsumeSeed(consumedKeyRef.current, searchKey)) return;

    const seed = sourceActionCardId
      ? resolveActionCardSeed({ card, storeLoaded })
      : autoPrompt
        ? ({
            kind: 'fallback',
            draft: { content: autoPrompt, draftDocument: undefined },
          } as const)
        : ({ kind: 'wait' } as const);
    if (seed.kind === 'wait') return;
    consumedKeyRef.current = searchKey;

    let cancelled = false;
    const generation = (runRef.current += 1);
    const isCurrent = () => !cancelled && runRef.current === generation;

    const chain = writeChainRef.current;
    const written = (chain.get(draftId) ?? Promise.resolve()).then(() =>
      drafts.updateDraft({
        draftId,
        ...mergeSeedIntoDraft(drafts.getDraft(draftId), seed.draft),
      })
    );
    chain.set(
      draftId,
      written.then(
        () => undefined,
        () => undefined
      )
    );

    void written.then(
      () => {
        if (isCurrent()) clearSearch();
      },
      (error: unknown) => {
        if (isCurrent()) consumedKeyRef.current = undefined;
        logError(error, 'Failed to seed the composer from an action card', {
          context: 'useActionCardPrefill',
        });
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    autoPrompt,
    sourceActionCardId,
    card,
    storeLoaded,
    draftId,
    drafts,
    clearSearch,
  ]);
};
